import React, { createContext, useContext, useEffect, ReactNode, useCallback } from 'react';
import webSocketService from '../services/WebSocketService';
import { useAppSettings } from './AppSettingsContext';

// Define the type for the WebSocket context
interface WebSocketContextType {
    sendMessage: (message: any) => void;
    onMessage: (event: string, callback: Function) => void;
    offMessage: (event: string, callback: Function) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
    children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
    const { settings, appSettingsReady, loadSettings } = useAppSettings();

    useEffect(() => {
        // Ensure app settings are ready before establishing WebSocket connection
        if (!appSettingsReady) return;

        // Disable WebSocket if the setting is enabled
        if (settings.disableWebSocket) {
            webSocketService.close();
            return;
        }

        // Determine the WebSocket URL based on settings
        if (settings.apiWebSocketUrl && settings.apiWebSocketUrl.startsWith('ws')) {
            console.log('Connecting to WebSocket at:', settings.apiWebSocketUrl);
            webSocketService.connect(settings.apiWebSocketUrl);
        } else {
            const relativeUrl = settings.apiWebSocketUrl || '/ws/refill_station/';
            const connectionUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${relativeUrl}`;
            console.log('Connecting to WebSocket at:', connectionUrl);
            webSocketService.connect(connectionUrl);
        }

        // Handler for 'settings_updated' event
        const handleSettingsUpdated = () => {
            console.log('Received settings_updated event. Refreshing app settings.');
            loadSettings(); // Trigger the settings refresh
        };

        // Register the event listener
        webSocketService.onMessage('settings_updated', handleSettingsUpdated);

        // Cleanup on unmount
        return () => {
            webSocketService.offMessage('settings_updated', handleSettingsUpdated);
            webSocketService.close();
        };
    }, [appSettingsReady, settings.apiWebSocketUrl, settings.disableWebSocket, loadSettings]);

    // Memoize the sendMessage function
    const sendMessage = useCallback((message: any) => {
        webSocketService.sendMessage(message);
    }, []);

    // Memoize the onMessage function
    const onMessage = useCallback((event: string, callback: Function) => {
        webSocketService.onMessage(event, callback);
    }, []);

    // Memoize the offMessage function
    const offMessage = useCallback((event: string, callback: Function) => {
        webSocketService.offMessage(event, callback);
    }, []);

    return (
        <WebSocketContext.Provider value={{ sendMessage, onMessage, offMessage }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (context === null) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};
